/**
 * Accessibility Context — Universal & Specialized Support
 * ────────────────────────────────────────────────────────────────
 * Centralised store for all visual / cognitive / interaction
 * accessibility preferences. State persists in localStorage so a
 * student who needs dyslexia mode + quiet mode never has to
 * re-configure them between visits.
 *
 * Why a Context (and not a plain hook)?
 *   • The same preferences must drive both the body class names
 *     AND read-aloud (TTS), suggested filters on the search page,
 *     and the autism path opt-in copy.
 *   • A single source of truth means features added later
 *     (e.g. a Reading Guide overlay) just `useAccessibility()`
 *     and respect the user's quiet-mode without duplication.
 *
 * Settings model:
 *   {
 *     dyslexiaFont:   boolean   // toggles OpenDyslexic-style font + spacing
 *     highContrast:   boolean   // pure-black/white override
 *     colorBlindMode: 'off' | 'deuteranopia' | 'protanopia' | 'tritanopia'
 *     fontScale:      0.85 | 1 | 1.15 | 1.3   // ⁻⁻ / − / 0 / + / ++
 *     quietMode:      boolean   // stops animation + simplifies UI
 *     readAloud:      boolean   // global TTS on hover/focus (opt-in)
 *     underlineLinks: boolean   // WCAG link-distinction safety net
 *   }
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const STORAGE_KEY = 'lwt.a11y.v1';

const DEFAULTS = {
  dyslexiaFont:   false,
  highContrast:   false,
  colorBlindMode: 'off',
  fontScale:      1,
  quietMode:      false,
  readAloud:      false,
  underlineLinks: false,
};

const AccessibilityContext = createContext({ ...DEFAULTS, set: () => {}, reset: () => {} });

function readInitial() {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
}

export function AccessibilityProvider({ children }) {
  const [prefs, setPrefs] = useState(readInitial);

  // Persist + push CSS-driving classes onto <html> so styles can hook in
  // declaratively (no per-component plumbing).
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* quota */ }

    const root = document.documentElement;
    root.classList.toggle('a11y-dyslexia',       prefs.dyslexiaFont);
    root.classList.toggle('a11y-high-contrast',  prefs.highContrast);
    root.classList.toggle('a11y-quiet',          prefs.quietMode);
    root.classList.toggle('a11y-underline-links', prefs.underlineLinks);
    // Reset → set single colour-blind filter class
    root.classList.remove('a11y-cb-deuteranopia', 'a11y-cb-protanopia', 'a11y-cb-tritanopia');
    if (prefs.colorBlindMode !== 'off') {
      root.classList.add(`a11y-cb-${prefs.colorBlindMode}`);
    }
    // Font scale via CSS variable
    root.style.setProperty('--a11y-font-scale', prefs.fontScale);
  }, [prefs]);

  const set = useCallback((patch) => setPrefs((p) => ({ ...p, ...patch })), []);
  const reset = useCallback(() => setPrefs(DEFAULTS), []);

  const value = useMemo(() => ({ ...prefs, set, reset }), [prefs, set, reset]);

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>;
}

export function useAccessibility() {
  return useContext(AccessibilityContext);
}
